import {
    chipsPerRubyForFormat,
    LEAGUE_GAME_KEYS,
    LEAGUE_GAME_META,
    LEAGUE_GAME_MIN_STAKE,
    LEAGUE_MIN_REAL_JOINERS,
    LEAGUE_MOVEMENT_TTL_MS,
    LEAGUE_PRIZE_SPLITS,
    LEAGUE_PRIZE_SPLIT_DEFAULT,
    LEAGUE_XP_PLACEMENT_BONUS,
    PRIZE_COINS_PER_RUBY,
    SEAT_TOPUP_AVAILABLE_AT_OR_BELOW,
    SEAT_TOPUP_RUBIES,
    seatTopUpMax,
} from "./constants";
import type { Match } from "../../Games/MatchCards/types";
import { getMatchStatus, isCricketBreakInPlayMatchStatus } from "../../Games/matchUtils";
import { isCricketMatchFinished } from "./leagueLiveFixtures";
import { leagueLeaderboardBarStatsForMember } from "./leagueLeaderboardBar";
import type { League, LeagueFixture, LeagueGameKey, LeagueMember, LeaguePlay, LeagueStatus, LeagueTableRow } from "./types";

function openStakeOnPlay(play: LeaguePlay): number {
    return Math.max(0, Math.floor(Number(play.stake) || 0));
}

/** Open stakes already committed to plays — they do not block a seat top-up. */
function committedOpenStakeForTopUp(league: League, viewerUserId: number): number {
    return (league.plays || [])
        .filter((play) => play.status === "open" && play.userId === viewerUserId)
        .reduce((total, play) => total + openStakeOnPlay(play), 0);
}

/**
 * Spendable chips that count toward the seat top-up threshold. Open Squares,
 * Trader, and other holds are excluded until they settle (or are edited down).
 */
export function seatChipsCountingTowardTopUp(league: League, viewerUserId: number): number {
    const member = league.members.find((row) => row.userId === viewerUserId);
    if (!member) return 0;
    const coins = Math.max(0, Math.floor(Number(member.coins) || 0));
    return Math.max(0, coins - committedOpenStakeForTopUp(league, viewerUserId));
}

export function formatCoins(value: number): string {
    return Math.round(value).toLocaleString();
}

/** Match format overs for seat sizing (null → default T20 rate). */
export function leagueFormatOvers(league: League | null | undefined): number | null {
    const raw = league?.fixture?.formatOvers;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
}

/** Chips credited for one seat top-up pack (format-aware). */
export function seatTopUpChips(formatOvers?: number | null): number {
    return SEAT_TOPUP_RUBIES * chipsPerRubyForFormat(formatOvers);
}

/** Prize-pot coins added for one seat top-up pack. */
export function seatTopUpPrizeCoins(): number {
    return SEAT_TOPUP_RUBIES * PRIZE_COINS_PER_RUBY;
}

export function memberSeatTopups(member: LeagueMember | null | undefined): number {
    return Math.max(0, Math.floor(Number(member?.seatTopups) || 0));
}

export function memberSeatTopupsRemaining(
    member: LeagueMember | null | undefined,
    formatOvers?: number | null,
): number {
    return Math.max(0, seatTopUpMax(formatOvers) - memberSeatTopups(member));
}

/**
 * True when a match-scoped league's cricket fixture is finished (or stamped finished)
 * even if the league is still `live` while plays settle / results publish.
 */
export function leagueFixtureIsFinishedForTopUp(
    league: Pick<League, "scope" | "matchFinishedAt">,
    opts?: { fixtureFinished?: boolean },
): boolean {
    if (league.scope !== "match") return false;
    return Boolean(opts?.fixtureFinished) || Boolean(league.matchFinishedAt);
}

/**
 * Match finished, league still live — plays grading / prize not paid yet.
 * Player-facing status: "RESULTS SOON" (not FINISHED / Ended).
 */
export function leagueIsAwaitingFinalResults(
    league: League,
    now: number = Date.now(),
    opts?: { fixtureFinished?: boolean },
): boolean {
    if (isLeagueRefunded(league, now)) return false;
    if (leagueStatus(league, now) !== "live") return false;
    return leagueFixtureIsFinishedForTopUp(league, opts);
}

/**
 * True when join is closed — mirrors `joinLeagueTx` (ended / cancelled / match finished).
 * Hub cards and invite landings must hide Join once RESULTS SOON, not only after settle.
 */
export function leagueJoiningClosed(
    league: League,
    now: number = Date.now(),
    opts?: { fixtureFinished?: boolean },
): boolean {
    if (isLeagueRefunded(league, now)) return true;
    if (leagueStatus(league, now) === "ended") return true;
    return leagueIsAwaitingFinalResults(league, now, opts);
}

/** True when the viewer may open the seat top-up modal (joined, league still active). */
export function canOpenLeagueSeatTopUp(
    league: League,
    viewerUserId: number,
    now: number = Date.now(),
    opts?: { fixtureFinished?: boolean },
): boolean {
    if (isLeagueRefunded(league, now)) return false;
    const status = leagueStatus(league, now);
    if (status === "ended") return false;
    // Match finished / settling — no more top-ups while results may still be unpublished.
    if (leagueFixtureIsFinishedForTopUp(league, opts)) return false;
    if (league.membership !== "host" && league.membership !== "member") return false;
    return Boolean(league.members.find((row) => row.userId === viewerUserId));
}

/**
 * Top-up opens once spendable chips are at or below
 * {@link SEAT_TOPUP_AVAILABLE_AT_OR_BELOW}. 0 when every included game is free —
 * nothing to top up for. Mirrors `seatTopUpAvailableAtOrBelow` on the backend.
 */
export function leagueSeatTopUpAvailableAtOrBelow(league: Pick<League, "includedGames">): number {
    const largestMin = leagueIncludedGames(league).reduce(
        (max, game) => Math.max(max, LEAGUE_GAME_MIN_STAKE[game]),
        0,
    );
    if (largestMin <= 0) return 0;
    return SEAT_TOPUP_AVAILABLE_AT_OR_BELOW;
}

/**
 * True when the viewer can complete a seat top-up purchase: under the pack cap and
 * spendable chips at or below the restock threshold. Open Squares stakes (including
 * overs that have not started) do not count — same as Trader holds.
 */
export function canPurchaseLeagueSeatTopUp(
    league: League,
    viewerUserId: number,
    now: number = Date.now(),
    opts?: { fixtureFinished?: boolean },
): boolean {
    if (!canOpenLeagueSeatTopUp(league, viewerUserId, now, opts)) return false;
    const member = league.members.find((row) => row.userId === viewerUserId);
    if (!member || memberSeatTopupsRemaining(member, leagueFormatOvers(league)) <= 0) return false;
    const atOrBelow = leagueSeatTopUpAvailableAtOrBelow(league);
    if (atOrBelow <= 0) return false;
    return seatChipsCountingTowardTopUp(league, viewerUserId) <= atOrBelow;
}

/** @deprecated Prefer {@link canPurchaseLeagueSeatTopUp}. */
export function canTopUpLeagueSeat(
    league: League,
    viewerUserId: number,
    now: number = Date.now(),
): boolean {
    return canPurchaseLeagueSeatTopUp(league, viewerUserId, now);
}

/** Roster line: paid seats, e.g. "1 joined". */
export function formatJoinedCountLabel(count: number): string {
    return `${count} joined`;
}

/** Unsettled stakes across the league — not a people count. */
export function formatOpenPlaysCountLabel(count: number): string {
    return count === 1 ? "1 play open" : `${count} plays open`;
}

export const OPEN_PLAYS_COUNT_HINT =
    "Unsettled Sixes, Bullion, Trader or Squares+ plays — balls are locked until they settle.";

/**
 * The first league the viewer hosts or joined for a given fixture, if any.
 * Pure over any `leagues` array so it works with both the prototype and the API snapshot.
 */
export function findViewerLeagueForFixture(
    leagues: League[],
    fixtureId: string | null | undefined,
    excludeLeagueId?: string,
): League | null {
    if (!fixtureId) return null;
    return (
        leagues.find(
            (league) =>
                league.id !== excludeLeagueId &&
                (league.membership === "host" || league.membership === "member") &&
                league.fixture?.id === fixtureId,
        ) ?? null
    );
}

/** Signed coin P/L, e.g. "+1,250" / "-400" / "0". */
export function formatPl(value: number): string {
    const rounded = Math.round(value);
    if (rounded === 0) return "0";
    return `${rounded > 0 ? "+" : "−"}${Math.abs(rounded).toLocaleString()}`;
}

/** Non-negative WON points for standings chrome, e.g. "3,349". */
export function formatWonPoints(value: number): string {
    return Math.max(0, Math.round(value)).toLocaleString();
}

/** Uppercase ordinal for activity / podium chrome, e.g. "1ST" / "2ND" / "3RD". */
export function formatLeagueRankLabel(rank: number): string {
    const n = Math.max(0, Math.round(rank));
    if (n <= 0) return "";
    const mod100 = n % 100;
    let suffix = "TH";
    if (mod100 < 11 || mod100 > 13) {
        const mod10 = n % 10;
        if (mod10 === 1) suffix = "ST";
        else if (mod10 === 2) suffix = "ND";
        else if (mod10 === 3) suffix = "RD";
    }
    return `${n}${suffix}`;
}

export function plTextClass(value: number): string {
    if (value > 0) return "text-[#32F78C]";
    if (value < 0) return "text-[#F73261]";
    return "text-white/70";
}

/** WON points accent — same green as the leaderboard WON column. */
export function wonPointsTextClass(value: number): string {
    return value > 0 ? "text-[#32F78C]" : "text-white/50";
}

/**
 * Seat chips on create/join. Fixed per match format — does not scale with entry fee.
 * `entryFeeRubies` is kept for call-site compatibility; prize pot still uses the fee.
 */
export function startingCoinsFor(_entryFeeRubies?: number, formatOvers?: number | null): number {
    return chipsPerRubyForFormat(formatOvers);
}

export function leaguePrizePotCoinsForSeats(entryFeeRubies: number, seats: number): number {
    return Math.max(0, Math.round(entryFeeRubies)) * Math.max(0, seats) * PRIZE_COINS_PER_RUBY;
}

/**
 * Coin prize pot for display / payouts.
 * Prefer the stored pot (entries + seat top-ups from the API); fall back to
 * entry×seats when older prototype rows omit `prizePotCoins`.
 */
export function leaguePrizePotCoins(league: League): number {
    const stored = league.prizePotCoins;
    if (stored != null && Number.isFinite(Number(stored))) {
        return Math.max(0, Math.round(Number(stored)));
    }
    return leaguePrizePotCoinsForSeats(league.entryFeeRubies, league.members.length);
}

/** Prize pot after this viewer pays the same entry as everyone else. */
export function leaguePrizePotCoinsAfterJoin(league: League): number {
    const entryBump = Math.max(0, Math.round(league.entryFeeRubies)) * PRIZE_COINS_PER_RUBY;
    return leaguePrizePotCoins(league) + entryBump;
}

export function memberPl(member: LeagueMember): number {
    return member.coins - member.startingCoins;
}

export function memberTotals(
    member: LeagueMember,
    games: LeagueGameKey[] = LEAGUE_GAME_KEYS,
): { wins: number; losses: number; played: number } {
    return games.reduce(
        (acc, key) => {
            const stats = member.byGame[key];
            if (!stats) return acc;
            acc.wins += stats.wins;
            acc.losses += stats.losses;
            acc.played += stats.played;
            return acc;
        },
        { wins: 0, losses: 0, played: 0 },
    );
}

/**
 * Games this fixture can actually host.
 * Omitted/`undefined` = all four (legacy prototype fixtures). Explicit `[]` = none available.
 */
export function fixtureAvailableGames(fixture: LeagueFixture | null | undefined): LeagueGameKey[] {
    if (fixture == null) return [];
    const listed = fixture.availableGames;
    if (listed == null) return [...LEAGUE_GAME_KEYS];
    return LEAGUE_GAME_KEYS.filter((game) => listed.includes(game));
}

/** Games that count in this league. Older saved leagues without the field default to all four. Always canonical order. */
export function leagueIncludedGames(league: Pick<League, "includedGames">): LeagueGameKey[] {
    const games = league.includedGames;
    const picked =
        !games || games.length === 0 ? [...LEAGUE_GAME_KEYS] : games.filter((game) => LEAGUE_GAME_KEYS.includes(game));
    return LEAGUE_GAME_KEYS.filter((game) => picked.includes(game));
}

/** Short label for cards and the create review step, e.g. "Sixes & Bullion" or "All games". */
export function formatIncludedGamesLabel(league: Pick<League, "includedGames">): string {
    const games = leagueIncludedGames(league);
    if (games.length === LEAGUE_GAME_KEYS.length) return "All games";
    if (games.length === 0) return "No games";
    if (games.length === 1) return `${LEAGUE_GAME_META[games[0]].label} only`;
    const labels = games.map((game) => LEAGUE_GAME_META[game].label);
    if (labels.length === 2) return `${labels[0]} & ${labels[1]}`;
    return `${labels.slice(0, -1).join(", ")} & ${labels[labels.length - 1]}`;
}

/**
 * Compact season line for League Mode chrome, e.g. "RUBY RUMBLE - R4".
 * Uses the last two words of the league name when long, plus a round from elapsed time.
 */
export function leagueModeSeasonLabel(league: Pick<League, "name" | "startsAt">, now: number = Date.now()): string {
    const words = league.name.trim().split(/\s+/).filter(Boolean);
    const short =
        words.length >= 3 ? words.slice(-2).join(" ") : words.length > 0 ? words.join(" ") : "League";
    const start = new Date(league.startsAt).getTime();
    const elapsed = Number.isFinite(start) ? Math.max(0, now - start) : 0;
    const round = Math.max(1, Math.floor(elapsed / (6 * 60 * 60 * 1000)) + 1);
    return `${short.toUpperCase()} - R${round}`;
}

export function leagueStatus(league: League, now: number = Date.now()): LeagueStatus {
    if (isLeagueCancelled(league) || isLeagueRefunded(league, now)) return "ended";
    if (league.settledAt || league.status === "ended") return "ended";
    const start = new Date(league.startsAt).getTime();
    const end = new Date(league.endsAt).getTime();
    if (Number.isFinite(end) && now >= end) return "ended";
    if (Number.isFinite(start) && now < start) return "upcoming";
    return "live";
}

/**
 * Card/header countdown. Live match leagues omit an end time — cricket finish is
 * unknown, and `endsAt` is only a durationHours estimate (extended while in play).
 */
export function leagueTimingLabel(league: League, now: number): string {
    if (isLeagueRefunded(league, now)) return "Cancelled · Rubies refunded";
    const status = leagueStatus(league, now);
    if (status === "live") {
        if (league.scope === "match") return "";
        return `Ends in ${formatCountdown(league.endsAt, now)}`;
    }
    if (status === "upcoming") return `Match in ${formatCountdown(league.startsAt, now)}`;
    return `Ended ${formatDateTime(league.endsAt)}`;
}

export function leagueStatusLabel(status: LeagueStatus): string {
    if (status === "live") return "Live";
    if (status === "upcoming") return "Prematch";
    return "Ended";
}

export type LeagueHubCardStatus = { label: string; className: string };

/**
 * Hub-card header. Match leagues stay `live` while plays settle (endsAt is pushed forward).
 * A finished fixture while the league is still live reads RESULTS SOON; true end → FINISHED.
 */
export function leagueHubCardStatus(
    league: League,
    now: number,
    opts?: { breakLabel?: string | null; fixtureFinished?: boolean },
): LeagueHubCardStatus {
    if (isLeagueRefunded(league, now)) return { label: "CANCELLED", className: "text-[#F73261]" };
    if (leagueIsAwaitingFinalResults(league, now, opts)) {
        return { label: "RESULTS SOON", className: "text-[#FFC72C]" };
    }
    const status = leagueStatus(league, now);
    if (status === "live" && opts?.breakLabel) return { label: opts.breakLabel, className: "text-[#a855f7]" };
    if (status === "live") return { label: "IN PLAY", className: "text-[#32F78D]" };
    if (status === "upcoming") {
        return {
            label: `STARTS IN ${formatCountdown(league.startsAt, now).toUpperCase()}`,
            className: "text-white/85",
        };
    }
    return { label: "FINISHED", className: "text-white/60" };
}

/**
 * Match centre strip (white games scorecard + sticky detail tabs). Shown for every non-refunded
 * match league — prematch renders the same scorecard with a countdown so upcoming and live match.
 */
export function leagueShowsMatchCentre(status: LeagueStatus): boolean {
    return status === "live" || status === "ended" || status === "upcoming";
}

export type LeagueFixtureDisplayStatus = {
    label: string;
    live: boolean;
    finished: boolean;
};

/** Lobby card + match strip status — respects league ended and finished cricket fixtures. */
export function leagueFixtureDisplayStatus(
    fixture: LeagueFixture,
    now: number,
    leagueStatus: LeagueStatus,
    liveMatch?: Match | null,
): LeagueFixtureDisplayStatus {
    if (leagueStatus === "ended" || (liveMatch && isCricketMatchFinished(liveMatch))) {
        return { label: "FINISHED", live: false, finished: true };
    }
    if (liveMatch) {
        const list = getMatchStatus({ ...liveMatch, sport: liveMatch.sport || "cricket" });
        if (isCricketBreakInPlayMatchStatus(list)) {
            const upper = list.text.toUpperCase();
            const label = /RAIN|WEATHER|TOSS DELAYED|WET OUTFIELD|COVERS/.test(upper)
                ? "RAIN DELAY"
                : list.text.replace(/^Break in Play\s*-?\s*/i, "MATCH BREAK").trim() || "MATCH BREAK";
            return { label, live: false, finished: false };
        }
        if (list.text === "IN-PLAY" || list.text.toUpperCase().includes("SUPER OVER")) {
            return { label: "IN-PLAY", live: true, finished: false };
        }
        if (list.text.startsWith("STARTS IN ") || /^in\s+/i.test(list.text)) {
            return {
                label: list.text.toUpperCase().startsWith("STARTS IN")
                    ? list.text.toUpperCase()
                    : `STARTS IN ${list.text.replace(/^in\s+/i, "").toUpperCase()}`,
                live: false,
                finished: false,
            };
        }
        if (list.text && list.text !== "IN-PLAY") {
            return { label: list.text, live: false, finished: false };
        }
    }
    const start = new Date(fixture.start).getTime();
    if (!Number.isFinite(start)) return { label: "SCHEDULED", live: false, finished: false };
    if (start <= now) {
        // Kickoff passed but no liveMatch / break status — keep scheduled until the feed confirms play.
        return { label: "SCHEDULED", live: false, finished: false };
    }
    return {
        label: `STARTS IN ${formatCountdown(fixture.start, now).toUpperCase()}`,
        live: false,
        finished: false,
    };
}

/** Players who paid to join, excluding the host, who seeds the league. */
export function leagueRealJoiners(league: Pick<League, "members">): number {
    return league.members.filter((member) => !member.isHost).length;
}

/** How many more real joiners are needed before the league can lock in and pay out. */
export function leagueJoinersNeeded(league: Pick<League, "members">): number {
    const min = league.minRealJoiners ?? LEAGUE_MIN_REAL_JOINERS;
    return Math.max(0, min - leagueRealJoiners(league));
}

/** Server already cancelled this league (kickoff under-fill or admin). */
export function isLeagueCancelled(league: Pick<League, "cancelledAt" | "status">): boolean {
    if (league.cancelledAt) return true;
    return league.status === "cancelled";
}

/**
 * Non-member opening a finished public league — show the public podium instead of join-closed.
 * Requires a server settle (`settledAt` / status ended). Cancelled leagues keep refund copy.
 */
export function isPublicLeagueSpectatorResults(
    league: Pick<League, "listing" | "membership" | "cancelledAt" | "status" | "settledAt">,
): boolean {
    if (league.listing !== "public") return false;
    if (league.membership === "host" || league.membership === "member") return false;
    if (isLeagueCancelled(league)) return false;
    return Boolean(league.settledAt) || league.status === "ended";
}

/**
 * A match league that reaches kickoff without the minimum real joiners is
 * cancelled: no play happens, no XP is granted, and every ruby debit (create
 * fee + entries) is reversed. Prefer `cancelledAt` from the API when present.
 */
export function isLeagueRefunded(league: League, now: number = Date.now()): boolean {
    if (isLeagueCancelled(league)) return true;
    if (league.scope !== "match") return false;
    const start = new Date(league.startsAt).getTime();
    if (!Number.isFinite(start) || now < start) return false;
    const min = league.minRealJoiners ?? LEAGUE_MIN_REAL_JOINERS;
    return leagueRealJoiners(league) < min;
}

function isIsoOnLocalCalendarDay(dateStr: string | undefined | null, day: Date = new Date()): boolean {
    if (!dateStr?.trim()) return false;
    const t = new Date(dateStr).getTime();
    if (!Number.isFinite(t)) return false;
    const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0).getTime();
    const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999).getTime();
    return t >= dayStart && t <= dayEnd;
}

/**
 * My Games drawer PAST tab: same “today only” rule as settled games — show an ended /
 * cancelled league when pot settle, match finish, scheduled end, or cancel falls on the
 * local calendar day. Full My Zone history still lists every past league.
 */
export function isEndedLeagueForMyGamesDrawer(
    league: Pick<League, "settledAt" | "matchFinishedAt" | "endsAt" | "cancelledAt">,
    day: Date = new Date(),
): boolean {
    return (
        isIsoOnLocalCalendarDay(league.settledAt, day) ||
        isIsoOnLocalCalendarDay(league.matchFinishedAt, day) ||
        isIsoOnLocalCalendarDay(league.endsAt, day) ||
        isIsoOnLocalCalendarDay(league.cancelledAt, day)
    );
}

/**
 * XP only becomes claimable once the league has started and cleared the
 * minimum-joiner bar (so it can never be refunded). Before that, nothing is
 * awarded — this is the anti-farm guard.
 */
export function isLeagueXpLive(league: League, now: number = Date.now()): boolean {
    if (isLeagueRefunded(league, now)) return false;
    return leagueStatus(league, now) !== "upcoming";
}

export type LeaguePlacementActivity = {
    userId: number;
    /** 1-based podium slot (prize / XP order among eligible finishers). */
    place: number;
    at: string;
    member: LeagueMember;
};

/**
 * Final podium rows once the league has settled. Eligible = at least one settled play
 * (same gate as prize coins / XP). All-games filter only — not game-specific.
 */
export function leaguePlacementPastMembers(
    league: League,
    opts: {
        scope?: "all" | "mine";
        viewerUserId?: number;
        gameFilter?: "all" | LeagueGameKey;
    } = {},
): LeaguePlacementActivity[] {
    const scope = opts.scope ?? "all";
    const gameFilter = opts.gameFilter ?? "all";
    if (gameFilter !== "all") return [];

    const at = league.settledAt?.trim();
    if (!at) return [];

    const settledPlaysByUser = new Map<number, number>();
    for (const play of league.plays) {
        if (play.status !== "settled") continue;
        settledPlaysByUser.set(play.userId, (settledPlaysByUser.get(play.userId) ?? 0) + 1);
    }

    const podiumSize = LEAGUE_XP_PLACEMENT_BONUS.length;
    // Standings order among members with ≥1 settled play (same gate as prize / XP).
    const eligible = leagueTableRows(league).filter(
        (row) => (settledPlaysByUser.get(row.userId) ?? 0) > 0,
    );
    const podium: LeaguePlacementActivity[] = [];
    for (let i = 0; i < eligible.length && i < podiumSize; i += 1) {
        const row = eligible[i];
        if (scope === "mine" && opts.viewerUserId != null && row.userId !== opts.viewerUserId) {
            continue;
        }
        podium.push({
            userId: row.userId,
            place: i + 1,
            at,
            member: row,
        });
    }
    return podium;
}

/** Distinct settled games for a member (activity / stats helpers). */
export function leagueDistinctSettledGames(league: Pick<League, "plays">, userId: number): number {
    const games = new Set<LeagueGameKey>();
    for (const play of league.plays) {
        if (play.userId === userId && play.status === "settled") {
            games.add(play.game);
        }
    }
    return games.size;
}

/**
 * Rank by WON points descending; ties share a rank and fall back to pot, then name,
 * so the table order stays stable between realtime updates.
 */
function recentMovement(previousRank: number | undefined, movedAt: string | undefined, rank: number): number {
    if (previousRank == null || movedAt == null) return 0;
    const age = Date.now() - new Date(movedAt).getTime();
    if (!Number.isFinite(age) || age > LEAGUE_MOVEMENT_TTL_MS) return 0;
    return previousRank - rank;
}

export function leagueTableRows(league: League): LeagueTableRow[] {
    const enriched = league.members.map((member) => {
        const totals = memberTotals(member, leagueIncludedGames(league));
        const open = league.plays.filter((play) => play.status === "open" && play.userId === member.userId);
        const openStake = open.reduce((total, play) => total + play.stake, 0);
        const bar = leagueLeaderboardBarStatsForMember(league, member.userId);
        return {
            ...member,
            ...totals,
            pl: memberPl(member),
            wonPoints: bar.wonPoints,
            potPoints: bar.potPoints,
            rank: 0,
            openPlays: open.length,
            openStake,
            availableCoins: Math.max(0, member.coins - openStake),
            movement: 0,
        };
    });
    enriched.sort((a, b) => {
        if (b.wonPoints !== a.wonPoints) return b.wonPoints - a.wonPoints;
        if (b.potPoints !== a.potPoints) return b.potPoints - a.potPoints;
        if (b.pl !== a.pl) return b.pl - a.pl;
        return a.playerName.localeCompare(b.playerName);
    });
    let lastWon: number | null = null;
    let lastRank = 0;
    enriched.forEach((row, index) => {
        if (lastWon != null && row.wonPoints === lastWon) {
            row.rank = lastRank;
        } else {
            row.rank = index + 1;
            lastRank = row.rank;
            lastWon = row.wonPoints;
        }
        row.movement = recentMovement(row.previousRank, row.rankMovedAt, row.rank);
    });
    return enriched;
}

/**
 * True once anyone has WON points, a settled play, or a live Bullion slip score.
 * Open 0-score Sixes/Bullion cards issued before toss do not count — toss-settled
 * scores do, even while the league is still `upcoming` (kickoff has not happened yet).
 * Open Bullion if-now place bonus does not count (that is not Overall until settle).
 * Live pick scores do count toward Overall.
 */
export function leagueLeaderboardHasScores(league: League): boolean {
    if (leagueTableRows(league).some((row) => row.wonPoints > 0 || row.played > 0)) return true;
    return league.plays.some((play) => {
        if (play.game !== "bullion" || play.status !== "open") return false;
        const settlement = play.marketDetails?.settlement;
        const slip =
            settlement?.slipScore != null && Number.isFinite(Number(settlement.slipScore))
                ? Number(settlement.slipScore)
                : Number(settlement?.points);
        return Number.isFinite(slip) && slip > 0;
    });
}

/**
 * Whether activity ranks / leaderboard bars are meaningful yet.
 */
export function leagueActivityRanksReady(league: League, now: number = Date.now()): boolean {
    if (isLeagueRefunded(league, now)) return false;
    return leagueLeaderboardHasScores(league);
}

/** Coin payout per finishing position from the ruby-funded pot. */
export function leaguePrizeSplit(league: League): number[] {
    const pot = leaguePrizePotCoins(league);
    const shares = LEAGUE_PRIZE_SPLITS[league.members.length] ?? LEAGUE_PRIZE_SPLIT_DEFAULT;
    return shares.map((share) => Math.round(pot * share));
}

export function viewerRow(league: League): LeagueTableRow | null {
    return leagueTableRows(league).find((row) => row.isViewer) ?? null;
}

function pluralise(value: number, unit: string): string {
    return `${value} ${unit}${value === 1 ? "" : "s"}`;
}

/** "2d 4h", "4h 12m", "38s" — coarse countdown for cards and headers. */
export function formatCountdown(targetIso: string, now: number = Date.now()): string {
    const target = new Date(targetIso).getTime();
    if (!Number.isFinite(target)) return "";
    const ms = target - now;
    if (ms <= 0) return "0m";
    const totalMinutes = Math.floor(ms / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (totalMinutes > 0) return `${totalMinutes}m`;
    return `${Math.max(1, Math.floor(ms / 1000))}s`;
}

export function formatDateTime(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });
}

export function formatTimeAgo(iso: string, now: number = Date.now()): string {
    const at = new Date(iso).getTime();
    if (!Number.isFinite(at)) return "";
    const seconds = Math.max(0, Math.floor((now - at) / 1000));
    if (seconds < 10) return "just now";
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
}

/** "1 day", "3 days", "1 week 2 days" — human summary of a league window. */
export function formatDurationBetween(startIso: string, endIso: string): string {
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return "";
    const hours = Math.round((end - start) / 3600000);
    if (hours < 24) return pluralise(hours, "hour");
    const days = Math.round(hours / 24);
    if (days % 7 === 0) return pluralise(days / 7, "week");
    return pluralise(days, "day");
}

export function combineDateAndTime(date: string, time: string): Date {
    return new Date(`${date}T${time}`);
}

/** Local `YYYY-MM-DD` for date-only inputs. */
export function toDateInputValue(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** "Today", "Tomorrow", or "Wed 13 Aug" — groups fixture lists in the create flow. */
export function formatFixtureDayHeading(iso: string, now: number = Date.now()): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const startOfTarget = new Date(date);
    startOfTarget.setHours(0, 0, 0, 0);
    const dayDiff = Math.round((startOfTarget.getTime() - startOfToday.getTime()) / 86400000);
    if (dayDiff === 0) return "Today";
    if (dayDiff === 1) return "Tomorrow";
    return date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

/** "In 1h 20m" when close; otherwise "Today · 14:30" / "Tomorrow · …" / "Starts in …". */
export function formatFixtureStartLabel(iso: string, now: number = Date.now()): string {
    const at = new Date(iso).getTime();
    if (!Number.isFinite(at)) return "";
    const ms = at - now;
    if (ms <= 0) return "Under way";
    const countdown = formatCountdown(iso, now);
    // Near kickoff: countdown beats clock time so urgency is obvious in the picker.
    if (ms <= 6 * 60 * 60 * 1000) {
        return `In ${countdown}`;
    }
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    const startOfTarget = new Date(at);
    startOfTarget.setHours(0, 0, 0, 0);
    const dayDiff = Math.round((startOfTarget.getTime() - startOfToday.getTime()) / 86400000);
    if (dayDiff <= 1) {
        const time = new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
        return dayDiff === 0 ? `Today · ${time}` : `Tomorrow · ${time}`;
    }
    return `Starts in ${countdown}`;
}

/** Status line + colour for compact match-card headers in the league fixture picker. */
export function leagueFixtureCardStatus(
    fixture: LeagueFixture,
    now: number = Date.now(),
): { text: string; color: string } {
    const startMs = new Date(fixture.start).getTime();
    if (Number.isFinite(startMs) && startMs <= now) {
        return { text: "IN-PLAY", color: "text-pink" };
    }
    const label = formatFixtureStartLabel(fixture.start, now);
    if (label.startsWith("In ")) {
        return {
            text: `IN ${label.slice("In ".length).toUpperCase()}`,
            color: "text-[#32F78D]",
        };
    }
    if (label.startsWith("Starts in ")) {
        return {
            text: `STARTS IN ${label.slice("Starts in ".length).toUpperCase()}`,
            color: "text-[#32F78D]",
        };
    }
    return { text: label, color: "text-white/75" };
}

const DAY_MS = 86400000;
const MIN_LEAGUE_LEAD_MS = DAY_MS + 60000;

/** End timestamp for a duration preset card (18:00 local, bumped if too soon). */
export function presetEndPreviewIso(days: number, now: number = Date.now()): string {
    const end = new Date(now + days * DAY_MS);
    end.setHours(18, 0, 0, 0);
    if (end.getTime() - now <= MIN_LEAGUE_LEAD_MS) {
        end.setDate(end.getDate() + 1);
    }
    return end.toISOString();
}

/** Shorter date for preset cards, e.g. "Wed 13 Aug · 18:00". */
export function formatEndPreview(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    const day = date.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
    const time = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    return `${day} · ${time}`;
}

export const LEAGUE_END_TIME_OPTIONS = ["12:00", "15:00", "18:00", "20:00", "21:00"] as const;

export function memberInitial(name: string): string {
    return (name.trim().charAt(0) || "?").toUpperCase();
}
